import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { userAPI, roleAPI } from '../../Components/services/api';
import AddAdminModal from './Modals/AddAdminModal';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalItems: 0,
    totalPages: 0,
    pageSize: 20
  });

  // State for AddAdminModal
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [adminForm, setAdminForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roleId: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // Auto-refresh when a user is invited from elsewhere (e.g. layout sidebar)
  useEffect(() => {
    const handleRefreshUsers = () => {
      fetchUsers(pagination.currentPage);
    };
    window.addEventListener('refreshUsers', handleRefreshUsers);
    return () => window.removeEventListener('refreshUsers', handleRefreshUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage]);

  const fetchUsers = async (page = 0) => {
    try {
      setLoading(true);
      const response = await userAPI.getAll(page, 20);
      console.log('Users response:', response.data);
      setUsers(response.data.users || []);
      setPagination({
        currentPage: response.data.currentPage || 0,
        totalItems: response.data.totalItems || 0,
        totalPages: response.data.totalPages || 0,
        pageSize: response.data.pageSize || 20
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await roleAPI.getAll();
      console.log('Roles response:', response.data);
      setRoles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await userAPI.delete(userId);
      toast.success(`User ${userName} deleted successfully`);
      fetchUsers(pagination.currentPage);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate user "${userName}"?`)) return;
    try {
      await userAPI.deactivate(userId);
      toast.success(`User ${userName} deactivated successfully`);
      fetchUsers(pagination.currentPage);
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error(error.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const handleReactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reactivate user "${userName}"?`)) return;
    try {
      await userAPI.reactivate(userId);
      toast.success(`User ${userName} reactivated successfully`);
      fetchUsers(pagination.currentPage);
    } catch (error) {
      console.error('Error reactivating user:', error);
      toast.error(error.response?.data?.message || 'Failed to reactivate user');
    }
  };

  const handleResendInvitation = async (userId, userName) => {
    if (!window.confirm(`Resend invitation email to "${userName}"?`)) return;
    try {
      await userAPI.resendInvitation(userId);
      toast.success(`Invitation resent to ${userName}`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error(error.response?.data?.message || 'Failed to resend invitation');
    }
  };

  const getRoleDisplay = (user) => {
    return user.roleType || user.role?.name || user.roleName || 'user';
  };

  // AddAdminModal handlers
  const handleOpenAddAdmin = () => {
    setShowAddAdminModal(true);
  };

  const handleCloseAddAdmin = () => {
    setShowAddAdminModal(false);
    resetAdminForm();
  };

  const validateForm = () => {
    const errors = {};
    if (!adminForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!adminForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!adminForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(adminForm.email)) {
      errors.email = 'Email is invalid';
    }
    if (!adminForm.roleId) {
      errors.roleId = 'Please select a role';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    try {
      setActionLoading(true);
      const adminData = {
        firstName: adminForm.firstName,
        lastName: adminForm.lastName,
        email: adminForm.email,
        roleId: adminForm.roleId
      };
      await userAPI.invite(adminData);
      toast.success('User invited successfully! A welcome email with credentials has been sent.');
      setShowAddAdminModal(false);
      resetAdminForm();
      fetchUsers(pagination.currentPage);
    } catch (error) {
      console.error('Error inviting user:', error);
      const errorMessage = error.response?.data?.message || 'Failed to invite user';
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const resetAdminForm = () => {
    setAdminForm({
      firstName: '',
      lastName: '',
      email: '',
      roleId: ''
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAdminForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">User Management</h2>
          <p className="text-sm text-gray-500">Total: {pagination.totalItems} users</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleOpenAddAdmin}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Invite User
          </button>
          <button
            onClick={() => fetchUsers(pagination.currentPage)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                    <div className="text-xs text-gray-500">ID: {user.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {getRoleDisplay(user)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap gap-2">
                      {!user.isActive && (
                        <button
                          onClick={() => handleReactivateUser(user.id, user.fullName)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Reactivate
                        </button>
                      )}
                      {user.isActive && (
                        <button
                          onClick={() => handleDeactivateUser(user.id, user.fullName)}
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          Deactivate
                        </button>
                      )}
                      <button
                        onClick={() => handleResendInvitation(user.id, user.fullName)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.fullName)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing page {pagination.currentPage + 1} of {pagination.totalPages}
            <span className="ml-2 text-gray-500">
              ({pagination.totalItems} total users)
            </span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => fetchUsers(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 0}
              className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => fetchUsers(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages - 1}
              className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* AddAdminModal */}
      {showAddAdminModal && (
        <AddAdminModal
          adminForm={adminForm}
          formErrors={formErrors}
          actionLoading={actionLoading}
          onInputChange={handleInputChange}
          onSubmit={handleAdminSubmit}
          onClose={handleCloseAddAdmin}
          roles={roles}
          loadingRoles={loadingRoles}
        />
      )}
    </div>
  );
};

export default AdminUsers;